import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useLocalStorage from '@/hooks/useLocalStorage';
import { defaultProducts, defaultCategories, type AdminProduct, type AdminCategory } from '@/mocks/adminData';

function ProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useLocalStorage<AdminProduct[]>('admin_products', defaultProducts);
  const [categories] = useLocalStorage<AdminCategory[]>('admin_categories', defaultCategories);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('todos');
  const [deleteTarget, setDeleteTarget] = useState<AdminProduct | null>(null);

  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [categories]);

  const filtered = useMemo(() => {
    let result = products;
    if (filterCategory !== 'todos') {
      result = result.filter((p) => p.category === filterCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q));
    }
    return result;
  }, [products, filterCategory, search]);

  const handleToggleActive = (id: string) => {
    setProducts(products.map((p) => (p.id === id ? { ...p, active: !p.active } : p)));
  };

  const handleToggleFeatured = (id: string) => {
    setProducts(products.map((p) => (p.id === id ? { ...p, featured: !p.featured } : p)));
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setProducts(products.filter((p) => p.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Produtos</h1>
          <p className="text-sm text-foreground-500 mt-1">Gerencie os produtos do catálogo.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/produtos/novo')}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Novo produto
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            placeholder="Buscar por nome ou SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-9 py-2 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600">
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 rounded-lg text-sm border border-background-300 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="todos">Todas as categorias</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <span className="text-xs text-foreground-500 self-center whitespace-nowrap">
          {filtered.length} produto(s)
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-shopping-bag-2-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500 mb-1">Nenhum produto encontrado</p>
            {search || filterCategory !== 'todos' ? (
              <button
                type="button"
                onClick={() => { setSearch(''); setFilterCategory('todos'); }}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Limpar filtros
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/admin/produtos/novo')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Criar primeiro produto
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Produto</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 hidden sm:table-cell">Categoria</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 hidden md:table-cell">SKU</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Preço</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-500">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-500">Destaque</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={p.mainImage} alt={p.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
                        <span className="text-sm font-medium text-foreground-800 truncate max-w-[160px]">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-xs text-foreground-600">{categoryMap[p.category] || '—'}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-foreground-500 font-mono">{p.sku}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-sm font-semibold text-foreground-800">R$ {p.price.toFixed(2)}</span>
                        {p.promotionalPrice && (
                          <span className="ml-2 text-xs text-accent-600 line-through">R$ {p.promotionalPrice.toFixed(2)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(p.id)}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors whitespace-nowrap ${
                          p.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {p.active ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => handleToggleFeatured(p.id)}
                        className={`text-xs ${p.featured ? 'text-amber-500' : 'text-foreground-300'} hover:text-amber-600 transition-colors`}
                      >
                        <i className={`${p.featured ? 'ri-star-fill' : 'ri-star-line'} text-base`} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/produtos/${p.id}/editar`)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-500 hover:text-foreground-700 transition-colors"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(p)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-sm p-6">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-delete-bin-line text-lg text-red-500" />
            </div>
            <p className="text-sm text-foreground-700 text-center mb-1">Tem certeza que deseja excluir?</p>
            <p className="text-xs text-foreground-500 text-center mb-5">{deleteTarget.name}</p>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
              <button type="button" onClick={handleDelete} className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsPage;